import Date from "@components/Atoms/Date";
import DiffContent from "@components/Atoms/DiffContent";
import type { ClientArticleProps } from "@core/SitePresenter/SitePresenter";
import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import styled from "@emotion/styled";
import t from "@ext/localization/locale/translate";
import {
	AvatarFallback,
	AvatarLabel,
	AvatarLabelAvatar,
	AvatarLabelDescription,
	AvatarLabelTitle,
	getAvatarFallback,
} from "@ui-kit/Avatar";
import { Dialog, DialogContent, DialogHeaderTemplate } from "@ui-kit/Dialog";
import { Loader } from "@ui-kit/Loader";
import { LoadMoreTrigger } from "@ui-kit/LoadMoreTrigger";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarProvider,
} from "@ui-kit/Sidebar";
import { SwitchField } from "@ui-kit/Switch";
import { History as HistoryIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ArticleHistoryViewModel, OffsetDataLoader } from "../model/ArticleHistoryViewModel";

interface HistoryProps {
	item: ClientArticleProps;
	className?: string;
	onClose: () => void;
}

const SidebarContainer = styled(SidebarProvider)`
  height: 100%;
  min-height: unset;
  max-height: 100%;
  overflow: hidden;

  ul {
	list-style: none !important;
  }

  li {
    line-height: unset;
    margin-bottom: unset;
  }
`;

const StyledSwitchField = styled(SwitchField)`
	> div {
		width: 100%;
		max-width: 15rem;
	}

	label {
		line-height: 1.5;
	}
`;

const History = (props: HistoryProps) => {
	const { className, item, onClose } = props;
	const apiUrlCreator = ApiUrlCreatorService.value;

	const [showDiff, setShowDiff] = useState(true);
	const [isOpen, setIsOpen] = useState(true);
	const [data, setData] = useState<ArticleHistoryViewModel[]>(null);
	const [selectedVersion, setSelectedVersion] = useState<ArticleHistoryViewModel>(null);

	const isLoadingRef = useRef(false);
	const hasMoreRef = useRef(true);
	const offsetRef = useRef(0);

	const showDiffText = t("show-diffs");

	const loadData = useCallback(async () => {
		if (isLoadingRef.current) return;
		isLoadingRef.current = true;
		const response = await FetchService.fetch<OffsetDataLoader<ArticleHistoryViewModel>>(
			apiUrlCreator.getVersionControlFileHistoryUrl(item.ref.path, offsetRef.current),
		);
		if (!response.ok) {
			setIsOpen(false);
			hasMoreRef.current = false;
			return;
		}
		const data = await response.json();

		offsetRef.current = data.nextOffset;

		hasMoreRef.current = data.hasMore;
		setData((prev) => {
			const newData = [...(prev ?? []), ...(data?.items?.filter(Boolean) ?? [])];
			if (!prev?.length) setSelectedVersion(newData[0]);
			return newData;
		});
		isLoadingRef.current = false;
	}, [item.ref.path, apiUrlCreator.getVersionControlFileHistoryUrl]);

	useEffect(() => {
		loadData();
	}, [loadData]);

	const onOpenChange = useCallback(
		(open: boolean) => {
			setShowDiff(true);
			setIsOpen(open);
			if (!open) {
				setData(null);
				onClose();
			}
		},
		[onClose],
	);

	return (
		<Dialog onOpenChange={onOpenChange} open={isOpen}>
			<DialogContent size="FS">
				<DialogHeaderTemplate
					description={t("git.history.modal.description")}
					icon={HistoryIcon}
					title={t("git.history.modal.title")}
				/>
				<SidebarContainer>
					<Sidebar className="h-full overflow-y-" collapsible="none">
						<SidebarContent>
							<SidebarGroup>
								<SidebarGroupContent>
									<SidebarMenu>
										{data ? (
											data?.map((model) => (
												<SidebarMenuItem key={model.version}>
													<SidebarMenuButton
														className="h-auto"
														isActive={selectedVersion?.version === model.version}
														onClick={() => setSelectedVersion(model)}
													>
														<div className={className}>
															<AvatarLabel size="lg">
																<AvatarLabelAvatar>
																	<AvatarFallback uniqueId={model.author}>
																		{getAvatarFallback(model.author)}
																	</AvatarFallback>
																</AvatarLabelAvatar>
																<AvatarLabelTitle>
																	{model.author ?? ""}
																</AvatarLabelTitle>
																<AvatarLabelDescription>
																	<Date date={model.date} />
																</AvatarLabelDescription>
															</AvatarLabel>
															<div className="file-path">
																<DiffContent
																	changes={
																		model.filePath.diff ?? [
																			{ value: model.filePath.path },
																		]
																	}
																	isCode={false}
																	showDiff={showDiff}
																/>
															</div>
														</div>
													</SidebarMenuButton>
												</SidebarMenuItem>
											))
										) : (
											<Loader />
										)}
										<LoadMoreTrigger
											hasMore={hasMoreRef.current}
											loadingText={t("loading")}
											onLoad={loadData}
										/>
									</SidebarMenu>
								</SidebarGroupContent>
							</SidebarGroup>
						</SidebarContent>
						<SidebarFooter>
							<StyledSwitchField
								alignment="right"
								checked={showDiff}
								label={showDiffText}
								onCheckedChange={setShowDiff}
								size="sm"
							/>
						</SidebarFooter>
					</Sidebar>
					<main className="w-full max-w-full overflow-hidden">
						<div className="flex-1 h-full overflow-y-auto">
							<div className={className}>
								{selectedVersion && (
									<div className="diff-content">
										<DiffContent changes={selectedVersion?.content ?? []} showDiff={showDiff} />
									</div>
								)}
							</div>
						</div>
					</main>
				</SidebarContainer>
			</DialogContent>
		</Dialog>
	);
};

export default styled(History)`
	.diff-content {
		padding: 20px;
	}

	.show-diff-checkbox {
		gap: 0.7rem;
		height: 20%;
		width: 100%;
		display: flex;
		font-size: 13px;
		max-height: 70px;
		border-radius: var(--radius-small);
		padding-left: 15px;
		padding-bottom: 10px;
		padding-top: 10px;
		flex-direction: column;
		align-items: flex-start;
		justify-content: center;
		background: var(--color-menu-bg);
	}

	.checkbox span {
		cursor: pointer;
		color: #777;
		font-size: 13px;
		line-height: 1.2em;
		padding-left: 3.5px;
	}

	.username {
		font-size: 15px;
	}

	.user-data .date {
		font-weight: 400;
		color: var(--color-input-active-text) !important;
	}

	.file-path {
		font-size: 13px;
		font-weight: 300;
		word-break: break-all;
	}
`;
